(() => {
  if (window.location.pathname !== "/pages/legal") {
    return;
  }

  const params = new URLSearchParams(window.location.search);
  if (params.get("view") === "legal") {
    return;
  }

  params.set("view", "legal");
  const nextUrl = `${window.location.pathname}?${params.toString()}${window.location.hash}`;
  window.location.replace(nextUrl);
})();

document.addEventListener("DOMContentLoaded", () => {
  if (window.lucide) {
    window.lucide.createIcons({ attrs: { "aria-hidden": "true" } });
  }

  const reducedMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  const progress = document.querySelector("[data-reading-progress]");

  if (progress) {
    const updateReadingProgress = () => {
      const scrollTop = window.scrollY || document.documentElement.scrollTop;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progressValue = docHeight > 0 ? (scrollTop / docHeight) * 100 : 0;
      progress.style.width = `${Math.min(progressValue, 100)}%`;
    };

    updateReadingProgress();
    window.addEventListener("scroll", updateReadingProgress, { passive: true });
  }

  if (window.location.hash) {
    window.setTimeout(() => {
      const target = document.getElementById(window.location.hash.slice(1));
      target?.scrollIntoView();
    }, 200);
  }

  const faqTopicLinks = Array.from(document.querySelectorAll(".faq-topic-nav a[href^='#']"));
  const faqTopicSections = faqTopicLinks
    .map((link) => document.getElementById(link.getAttribute("href").slice(1)))
    .filter(Boolean);

  if (faqTopicLinks.length && faqTopicSections.length) {
    const setActiveFaqTopic = (id) => {
      faqTopicLinks.forEach((link) => {
        const isActive = link.getAttribute("href") === `#${id}`;
        link.classList.toggle("is-active", isActive);
        if (isActive) {
          link.setAttribute("aria-current", "true");
        } else {
          link.removeAttribute("aria-current");
        }
      });
    };

    const initialFaqId = window.location.hash
      ? window.location.hash.slice(1)
      : faqTopicSections[0].id;

    if (faqTopicSections.some((section) => section.id === initialFaqId)) {
      setActiveFaqTopic(initialFaqId);
    }

    faqTopicLinks.forEach((link) => {
      link.addEventListener("click", () => {
        const id = link.getAttribute("href")?.slice(1);
        if (id) setActiveFaqTopic(id);
      });
    });

    if ("IntersectionObserver" in window) {
      const faqTopicObserver = new IntersectionObserver(
        (entries) => {
          const visibleEntry = entries
            .filter((entry) => entry.isIntersecting)
            .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];

          if (visibleEntry?.target?.id) {
            setActiveFaqTopic(visibleEntry.target.id);
          }
        },
        { rootMargin: "-35% 0px -55% 0px", threshold: [0, 0.2, 0.6] }
      );

      faqTopicSections.forEach((section) => faqTopicObserver.observe(section));
    }
  }

  if (!reducedMotion) {
    document.querySelectorAll("[data-announcement-track]").forEach((track) => {
      let offerIndex = 0;
      const resetIndex = Math.max(1, track.children.length - 1);

      window.setInterval(() => {
        offerIndex += 1;
        track.style.transform = `translateX(-${offerIndex * 100}%)`;

        if (offerIndex !== resetIndex) return;
        track.addEventListener(
          "transitionend",
          () => {
            track.classList.add("no-transition");
            offerIndex = 0;
            track.style.transform = "translateX(0)";
            window.requestAnimationFrame(() => {
              window.requestAnimationFrame(() => track.classList.remove("no-transition"));
            });
          },
          { once: true }
        );
      }, 5000);
    });
  }

  const menuButton = document.querySelector("[data-menu-toggle]");
  const menu = document.querySelector("[data-menu]");

  menuButton?.addEventListener("click", () => {
    const isOpen = menu?.classList.toggle("is-open") ?? false;
    menuButton.setAttribute("aria-expanded", String(isOpen));
    menuButton.setAttribute("aria-label", isOpen ? "Close menu" : "Open menu");
  });

  menu?.querySelectorAll("a").forEach((link) => {
    link.addEventListener("click", () => {
      menu.classList.remove("is-open");
      menuButton?.setAttribute("aria-expanded", "false");
    });
  });

  const siteHeader = document.querySelector(".site-header");

  if (siteHeader) {
    const megaPairs = [
      { trigger: ".shop-trigger", menu: ".shop-menu", className: "is-shop-mega-open" },
      { trigger: ".brand-trigger", menu: ".brand-menu", className: "is-brand-mega-open" },
      { trigger: ".journal-trigger", menu: ".journal-menu", className: "is-journal-mega-open" }
    ];

    const closeAllMegas = () => {
      megaPairs.forEach(({ trigger, className }) => {
        siteHeader.classList.remove(className);
        siteHeader.querySelector(trigger)?.setAttribute("aria-expanded", "false");
      });
    };

    megaPairs.forEach(({ trigger: triggerSelector, menu: menuSelector, className }) => {
      const trigger = siteHeader.querySelector(triggerSelector);
      const mega = siteHeader.querySelector(menuSelector);
      if (!trigger || !mega) return;

      let closeTimer;

      trigger.setAttribute("aria-haspopup", "true");
      trigger.setAttribute("aria-expanded", "false");

      const setOpen = (isOpen) => {
        if (isOpen) closeAllMegas();
        siteHeader.classList.toggle(className, isOpen);
        trigger.setAttribute("aria-expanded", String(isOpen));
      };

      const isEngaged = () =>
        trigger.matches(":hover") ||
        mega.matches(":hover") ||
        document.activeElement === trigger ||
        mega.contains(document.activeElement);

      const open = () => {
        window.clearTimeout(closeTimer);
        setOpen(true);
      };

      const queueClose = (delay = 280) => {
        window.clearTimeout(closeTimer);
        closeTimer = window.setTimeout(() => {
          if (isEngaged()) return;
          setOpen(false);
        }, delay);
      };

      trigger.addEventListener("mouseenter", open);
      trigger.addEventListener("focus", open);
      trigger.addEventListener("mouseleave", () => queueClose());
      trigger.addEventListener("blur", () => queueClose());

      mega.addEventListener("mouseenter", open);
      mega.addEventListener("focusin", open);
      mega.addEventListener("mouseleave", () => queueClose());
      mega.addEventListener("focusout", () => queueClose());
      mega.querySelectorAll("a").forEach((link) => {
        link.addEventListener("click", closeAllMegas);
      });
    });

    siteHeader.addEventListener("keydown", (event) => {
      if (event.key !== "Escape") return;
      closeAllMegas();
      if (document.activeElement instanceof HTMLElement) {
        document.activeElement.blur();
      }
    });
  }

  document.querySelectorAll("[data-account-link]").forEach((link) => {
    link.addEventListener("click", (event) => {
      if (event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || link.target === "_blank") return;
      const loadingPanel = document.querySelector("[data-account-loading]");
      if (!loadingPanel) return;

      event.preventDefault();
      loadingPanel.hidden = false;
      window.requestAnimationFrame(() => loadingPanel.classList.add("is-visible"));
      window.setTimeout(() => {
        window.location.href = link.href;
      }, 180);
    });
  });

  document.querySelectorAll("[data-search-panel]").forEach((panel) => {
    const header = panel.closest(".site-header");
    const toggle = header?.querySelector("[data-search-toggle]");
    const closeButton = panel.querySelector("[data-search-close]");
    const input = panel.querySelector("[data-search-input]");
    const results = panel.querySelector("[data-search-results]");
    const status = panel.querySelector("[data-search-status]");
    const form = panel.querySelector("[data-search-form]");
    const searchUrl = panel.dataset.predictiveSearchUrl || "/search/suggest";
    const searchJsonUrl = searchUrl.endsWith(".json") ? searchUrl : `${searchUrl}.json`;
    let searchTimer;
    let searchController;

    if (!toggle || !input || !results || !status || !form) return;

    const escapeHtml = (value) =>
      String(value || "").replace(/[&<>"']/g, (character) => {
        const entities = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
        return entities[character];
      });

    const formatPrice = (value) => {
      const number = Number.parseFloat(value);
      if (Number.isNaN(number)) return "";
      return number.toLocaleString("de-DE", { style: "currency", currency: "EUR" });
    };

    const openSearch = () => {
      panel.hidden = false;
      toggle.setAttribute("aria-expanded", "true");
      window.requestAnimationFrame(() => panel.classList.add("is-open"));
      window.setTimeout(() => input.focus(), 80);
    };

    const closeSearch = () => {
      panel.classList.remove("is-open");
      toggle.setAttribute("aria-expanded", "false");
      window.setTimeout(() => {
        if (!panel.classList.contains("is-open")) panel.hidden = true;
      }, 180);
    };

    const renderResults = (data, query) => {
      const groups = [
        ["products", "Products"],
        ["articles", "Journal"],
        ["pages", "Pages"],
        ["collections", "Collections"]
      ];
      const resources = data?.resources?.results || {};
      const markup = groups
        .map(([key, label]) => {
          const items = Array.isArray(resources[key]) ? resources[key] : [];
          if (!items.length) return "";

          return `
            <div class="header-search-group">
              <p>${label}</p>
              ${items
                .slice(0, 4)
                .map((item) => {
                  const image = item?.featured_image?.url || item?.image;
                  const price = key === "products" && item?.price ? formatPrice(item.price) : "";
                  const body = item?.summary_html || item?.body || "";
                  const cleanBody = escapeHtml(body.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim()).slice(0, 96);
                  return `
                    <a class="header-search-result" href="${escapeHtml(item.url || "/search?q=" + encodeURIComponent(query))}">
                      ${
                        image
                          ? `<span class="header-search-result-image"><img src="${escapeHtml(image)}" alt="${escapeHtml(item.title)}" loading="lazy"></span>`
                          : `<span class="header-search-result-mark">${escapeHtml(label.slice(0, 1))}</span>`
                      }
                      <span class="header-search-result-copy">
                        <strong>${escapeHtml(item.title)}</strong>
                        ${cleanBody ? `<small>${cleanBody}</small>` : ""}
                      </span>
                      ${price ? `<span class="header-search-result-price">${price}</span>` : ""}
                    </a>
                  `;
                })
                .join("")}
            </div>
          `;
        })
        .join("");

      if (!markup) {
        status.textContent = "No results yet. Try mineral, reef, sunscreen or brand.";
        results.innerHTML = "";
        return;
      }

      status.textContent = "Search results";
      results.innerHTML = `${markup}<a class="header-search-all" href="/search?q=${encodeURIComponent(query)}">View all results</a>`;
    };

    const runSearch = (query) => {
      const trimmedQuery = query.trim();
      window.clearTimeout(searchTimer);

      if (trimmedQuery.length < 2) {
        if (searchController) searchController.abort();
        status.textContent = "Start typing to search.";
        results.innerHTML = "";
        return;
      }

      status.innerHTML = '<span class="mini-spinner" aria-hidden="true"></span> Searching';
      searchTimer = window.setTimeout(async () => {
        try {
          if (searchController) searchController.abort();
          searchController = new AbortController();
          const url = `${searchJsonUrl}?q=${encodeURIComponent(trimmedQuery)}&resources[type]=product,page,article,collection&resources[limit]=6`;
          const response = await fetch(url, {
            headers: { Accept: "application/json" },
            signal: searchController.signal
          });
          if (!response.ok) throw new Error("Search failed");
          renderResults(await response.json(), trimmedQuery);
        } catch (error) {
          if (error.name === "AbortError") return;
          status.textContent = "Search is taking a moment. Press enter to view all results.";
        }
      }, 220);
    };

    toggle.addEventListener("click", () => {
      if (panel.hidden || !panel.classList.contains("is-open")) {
        openSearch();
      } else {
        closeSearch();
      }
    });

    closeButton?.addEventListener("click", closeSearch);
    input.addEventListener("input", () => runSearch(input.value));
    form.addEventListener("submit", () => {
      if (!input.value.trim()) input.value = "";
    });

    document.addEventListener("click", (event) => {
      if (panel.hidden) return;
      const target = event.target;
      if (!(target instanceof Node)) return;
      if (panel.contains(target) || toggle.contains(target)) return;
      closeSearch();
    });

    document.addEventListener("keydown", (event) => {
      if (event.key !== "Escape" || panel.hidden) return;
      closeSearch();
      toggle.focus();
    });
  });

  const revealItems = document.querySelectorAll(".reveal");
  if (reducedMotion || !("IntersectionObserver" in window)) {
    revealItems.forEach((item) => item.classList.add("is-visible"));
  } else {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add("is-visible");
          observer.unobserve(entry.target);
        });
      },
      { threshold: 0.12, rootMargin: "0px 0px -40px" }
    );

    revealItems.forEach((item) => observer.observe(item));
  }

  document.querySelectorAll("[data-newsletter]").forEach((form) => {
    form.addEventListener("submit", (event) => {
      event.preventDefault();
      const email = form.querySelector("input[type='email']");
      const message = form.querySelector("[data-form-message]");
      if (!email || !message) return;

      message.className = "form-message";
      if (!email.validity.valid) {
        message.textContent = "Please enter a valid email address.";
        message.classList.add("is-error");
        email.focus();
        return;
      }

      message.textContent = "Thank you. You are on the list.";
      message.classList.add("is-success");
      form.reset();
    });
  });

  document.querySelectorAll("[data-campaign-gallery]").forEach((gallery) => {
    const track = gallery.querySelector("[data-campaign-track]");
    const slides = Array.from(gallery.querySelectorAll(".campaign-slide"));
    const buttons = Array.from(gallery.querySelectorAll("[data-campaign-index]"));
    if (!track || slides.length === 0) return;

    let campaignIndex = 0;

    const showCampaign = (nextIndex) => {
      campaignIndex = (nextIndex + slides.length) % slides.length;
      track.style.transform = `translateY(-${campaignIndex * 100}%)`;
      buttons.forEach((button, index) => {
        const isActive = index === campaignIndex;
        button.classList.toggle("is-active", isActive);
        button.setAttribute("aria-selected", String(isActive));
      });
    };

    buttons.forEach((button) => {
      button.addEventListener("click", () => {
        const nextIndex = Number.parseInt(button.dataset.campaignIndex || "0", 10);
        showCampaign(nextIndex);
      });
    });

    showCampaign(0);
  });

  const ugcGrid = document.querySelector("[data-ugc-grid]");
  const moveUgc = (direction) => {
    if (!ugcGrid) return;
    const firstItem = ugcGrid.firstElementChild;
    const gap = Number.parseFloat(getComputedStyle(ugcGrid).columnGap) || 0;
    const step = (firstItem?.getBoundingClientRect().width || ugcGrid.clientWidth / 5) + gap;
    ugcGrid.scrollBy({ left: direction * step, behavior: reducedMotion ? "auto" : "smooth" });
  };

  document.querySelector("[data-ugc-prev]")?.addEventListener("click", () => moveUgc(-1));
  document.querySelector("[data-ugc-next]")?.addEventListener("click", () => moveUgc(1));

  document.querySelectorAll("[data-product-gallery]").forEach((gallery) => {
    const mainImage = gallery.querySelector("[data-gallery-image]");
    const mainFrame = mainImage?.closest(".product-gallery-main");
    const thumbs = Array.from(gallery.querySelectorAll("[data-gallery-thumb]"));
    const prevButton = gallery.querySelector("[data-gallery-prev]");
    const nextButton = gallery.querySelector("[data-gallery-next]");
    if (!mainImage || !mainFrame) return;

    const slideMap = new Map([
      [
        0,
        {
          src: mainImage.getAttribute("src"),
          alt: mainImage.getAttribute("alt") || ""
        }
      ]
    ]);

    thumbs.forEach((thumb) => {
      const index = Number.parseInt(thumb.dataset.galleryIndex || "0", 10);
      slideMap.set(index, {
        src: thumb.dataset.gallerySrc,
        alt: thumb.dataset.galleryAlt || ""
      });
    });

    const slides = Array.from(slideMap.entries())
      .sort(([a], [b]) => a - b)
      .map(([, slide]) => slide)
      .filter((slide) => slide.src);

    if (slides.length < 2) return;

    let productGalleryIndex = 0;
    let imageSwapTimer;
    let isGalleryAnimating = false;
    let pendingGalleryRequest = null;

    const galleryZoomByIndex = new Map([
      [0, "1.22"],
      [1, "1.12"]
    ]);

    const getGalleryZoom = (index) => galleryZoomByIndex.get(index) || "1";

    const preloadProductSlide = (slide) =>
      new Promise((resolve) => {
        const preloadImage = new Image();
        let isSettled = false;
        const settle = () => {
          if (isSettled) return;
          isSettled = true;
          resolve();
        };

        preloadImage.onload = () => {
          if (!preloadImage.decode) {
            settle();
            return;
          }

          preloadImage.decode().then(settle).catch(settle);
        };
        preloadImage.onerror = settle;
        preloadImage.src = slide.src;

        if (preloadImage.complete) {
          preloadImage.onload();
        }
      });

    const updateGalleryThumbs = () => {
      gallery.dataset.galleryActive = String(productGalleryIndex);
      thumbs.forEach((thumb) => {
        const isActive = Number.parseInt(thumb.dataset.galleryIndex || "0", 10) === productGalleryIndex;
        thumb.classList.toggle("is-active", isActive);
        thumb.setAttribute("aria-current", isActive ? "true" : "false");
      });
    };

    const setProductImage = async (nextIndex, direction = 1, animate = true) => {
      const normalizedIndex = (nextIndex + slides.length) % slides.length;
      if (normalizedIndex === productGalleryIndex) return;
      if (isGalleryAnimating) {
        pendingGalleryRequest = { nextIndex: normalizedIndex, direction, animate };
        return;
      }
      const nextSlide = slides[normalizedIndex];
      const shouldAnimate = animate && !reducedMotion;
      const previousIndex = productGalleryIndex;

      window.clearTimeout(imageSwapTimer);
      isGalleryAnimating = true;
      await preloadProductSlide(nextSlide);
      productGalleryIndex = normalizedIndex;
      updateGalleryThumbs();

      const swapImage = () => {
        mainImage.src = nextSlide.src;
        mainImage.alt = nextSlide.alt;
        mainImage.style.setProperty("--gallery-zoom", getGalleryZoom(normalizedIndex));
        mainImage.style.setProperty("--gallery-slide-x", "0%");
      };

      if (!shouldAnimate) {
        swapImage();
        isGalleryAnimating = false;
        return;
      }

      mainImage.style.setProperty("--gallery-zoom", getGalleryZoom(previousIndex));
      mainImage.style.setProperty("--gallery-slide-x", "0%");

      const incomingImage = document.createElement("img");
      incomingImage.className = "product-hero-image product-hero-image-incoming";
      incomingImage.src = nextSlide.src;
      incomingImage.alt = "";
      incomingImage.setAttribute("aria-hidden", "true");
      incomingImage.decoding = "async";
      incomingImage.style.setProperty("--gallery-zoom", getGalleryZoom(normalizedIndex));
      incomingImage.style.setProperty("--gallery-slide-x", direction >= 0 ? "100%" : "-100%");
      mainFrame.appendChild(incomingImage);

      window.requestAnimationFrame(() => {
        mainImage.classList.add("is-slide-out");
        incomingImage.classList.add("is-slide-in");
        mainImage.style.setProperty("--gallery-slide-x", direction >= 0 ? "-100%" : "100%");
        incomingImage.style.setProperty("--gallery-slide-x", "0%");
      });

      let didFinishSlide = false;
      const finishSlide = () => {
        if (didFinishSlide) return;
        didFinishSlide = true;
        window.clearTimeout(imageSwapTimer);
        mainImage.classList.add("is-resetting");
        mainImage.getBoundingClientRect();
        swapImage();
        mainImage.classList.remove("is-slide-out");
        window.requestAnimationFrame(() => {
          window.requestAnimationFrame(() => {
            incomingImage.remove();
            mainImage.classList.remove("is-resetting");
            isGalleryAnimating = false;
            if (pendingGalleryRequest) {
              const request = pendingGalleryRequest;
              pendingGalleryRequest = null;
              setProductImage(request.nextIndex, request.direction, request.animate);
            }
          });
        });
      };

      incomingImage.addEventListener("transitionend", finishSlide, { once: true });
      imageSwapTimer = window.setTimeout(finishSlide, 1120);
    };

    thumbs.forEach((thumb) => {
      thumb.addEventListener("click", (event) => {
        const nextIndex = Number.parseInt(thumb.dataset.galleryIndex || "0", 10);
        const direction = nextIndex >= productGalleryIndex ? 1 : -1;
        setProductImage(nextIndex, direction, event.detail !== 0);
      });
    });

    prevButton?.addEventListener("click", (event) => {
      setProductImage(productGalleryIndex - 1, -1, event.detail !== 0);
    });

    nextButton?.addEventListener("click", (event) => {
      setProductImage(productGalleryIndex + 1, 1, event.detail !== 0);
    });

    mainImage.style.setProperty("--gallery-zoom", getGalleryZoom(productGalleryIndex));
    mainImage.style.setProperty("--gallery-slide-x", "0%");
    updateGalleryThumbs();
  });

  document.querySelectorAll("[data-landscape-gallery]").forEach((gallery) => {
    const track = gallery.querySelector("[data-landscape-track]");
    const slides = Array.from(track?.children || []);
    if (!track || slides.length < 2) return;

    let landscapeIndex = 0;
    let isLandscapeAnimating = false;
    const landscapeDuration = 1120;

    if (reducedMotion) {
      track.style.transition = "none";
    }

    const showLandscapeSlide = (nextIndex) => {
      const normalizedIndex = Math.max(0, Math.min(slides.length - 1, nextIndex));
      if (normalizedIndex === landscapeIndex) return;
      landscapeIndex = normalizedIndex;
      isLandscapeAnimating = true;
      track.style.setProperty("--landscape-index", String(landscapeIndex));
      gallery.classList.toggle("is-second-slide", landscapeIndex === 1);
      window.setTimeout(() => {
        isLandscapeAnimating = false;
      }, landscapeDuration);
    };

    const shouldLockLandscape = () => {
      const galleryRect = gallery.getBoundingClientRect();
      const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
      const lockLine = viewportHeight * 0.5;
      return galleryRect.top <= lockLine && galleryRect.bottom >= lockLine;
    };

    const handleLandscapeScrollIntent = (direction, event) => {
      if (!shouldLockLandscape()) return false;

      if (isLandscapeAnimating) {
        event?.preventDefault();
        return true;
      }

      if (direction > 0 && landscapeIndex < slides.length - 1) {
        event?.preventDefault();
        showLandscapeSlide(landscapeIndex + 1);
        return true;
      }

      if (direction < 0 && landscapeIndex > 0) {
        event?.preventDefault();
        showLandscapeSlide(landscapeIndex - 1);
        return true;
      }

      return false;
    };

    gallery.addEventListener(
      "wheel",
      (event) => {
        const direction = Math.sign(event.deltaY);
        if (direction !== 0) handleLandscapeScrollIntent(direction, event);
      },
      { passive: false }
    );

    let touchStartY = 0;
    let hasHandledTouchSwipe = false;
    const touchThreshold = 28;

    gallery.addEventListener(
      "touchstart",
      (event) => {
        touchStartY = event.touches?.[0]?.clientY || 0;
        hasHandledTouchSwipe = false;
      },
      { passive: true }
    );

    gallery.addEventListener(
      "touchmove",
      (event) => {
        if (!touchStartY) return;
        const currentY = event.touches?.[0]?.clientY || touchStartY;
        const deltaY = touchStartY - currentY;
        if (Math.abs(deltaY) < touchThreshold) return;

        const direction = Math.sign(deltaY);
        if (hasHandledTouchSwipe) {
          if (shouldLockLandscape() && isLandscapeAnimating) event.preventDefault();
          return;
        }

        hasHandledTouchSwipe = handleLandscapeScrollIntent(direction, event);
      },
      { passive: false }
    );

    gallery.addEventListener(
      "touchend",
      () => {
        touchStartY = 0;
        hasHandledTouchSwipe = false;
      },
      { passive: true }
    );

    track.style.setProperty("--landscape-index", "0");
  });

  const quantity = document.querySelector("[data-quantity]");
  const quantityValue = quantity?.querySelector("[data-quantity-value]");
  const quantityInput = document.querySelector("[data-quantity-input]");
  let currentQuantity = 1;

  const syncQuantity = () => {
    if (quantityValue) quantityValue.textContent = String(currentQuantity);
    if (quantityInput) quantityInput.value = String(currentQuantity);
  };

  quantity?.querySelector("[data-quantity-minus]")?.addEventListener("click", () => {
    currentQuantity = Math.max(1, currentQuantity - 1);
    syncQuantity();
  });

  quantity?.querySelector("[data-quantity-plus]")?.addEventListener("click", () => {
    currentQuantity = Math.min(10, currentQuantity + 1);
    syncQuantity();
  });

  syncQuantity();

  const initialCartCount = Number.parseInt(document.querySelector("[data-cart-count]")?.textContent || "0", 10);
  const hasShopifyCartForm = Boolean(document.querySelector('form[action*="/cart/add"]'));
  let cartCount = Number.isNaN(initialCartCount) ? 0 : initialCartCount;
  if (!hasShopifyCartForm) {
    try {
      cartCount = Number.parseInt(localStorage.getItem("natuerlich-cart") || String(cartCount), 10);
    } catch {
      cartCount = Number.isNaN(initialCartCount) ? 0 : initialCartCount;
    }
  }

  const updateCartCount = () => {
    document.querySelectorAll("[data-cart-count]").forEach((count) => {
      count.textContent = String(cartCount);
    });
  };

  updateCartCount();

  document.querySelector("[data-add-cart]")?.addEventListener("click", (event) => {
    const button = event.currentTarget;
    if (hasShopifyCartForm && button.closest('form[action*="/cart/add"]')) return;
    cartCount += currentQuantity;
    updateCartCount();
    try {
      localStorage.setItem("natuerlich-cart", String(cartCount));
    } catch {
      // The prototype still works when browser storage is unavailable.
    }
    button.textContent = "Added to cart";
    window.setTimeout(() => {
      button.textContent = "Add to cart";
    }, 1400);
  });

  const applicationCarousel = document.querySelector("[data-application-carousel]");

  if (applicationCarousel) {
    const track = applicationCarousel.querySelector("[data-application-track]");
    const slides = [...applicationCarousel.querySelectorAll("[data-application-slide]")];
    const nextButton = applicationCarousel.querySelector("[data-application-next]");

    if (track && nextButton && slides.length > 1) {
      const loopSlide = slides[0].cloneNode(true);
      loopSlide.setAttribute("aria-hidden", "true");
      loopSlide.removeAttribute("data-application-slide");
      track.appendChild(loopSlide);

      let applicationIndex = 0;
      let applicationIsMoving = false;

      const updateApplicationA11y = () => {
        const activeIndex = applicationIndex % slides.length;
        slides.forEach((slide, index) => {
          slide.setAttribute("aria-hidden", String(index !== activeIndex));
        });
        const nextIndex = (activeIndex + 1) % slides.length;
        nextButton.setAttribute("aria-label", `Show how to use step ${nextIndex + 1} of ${slides.length}`);
      };

      const setApplicationPosition = (index) => {
        track.style.transform = `translate3d(-${index * 100}%, 0, 0)`;
      };

      const jumpToApplicationSlide = (index) => {
        track.style.transition = "none";
        applicationIndex = index;
        setApplicationPosition(applicationIndex);
        updateApplicationA11y();
        window.requestAnimationFrame(() => {
          track.style.transition = "";
        });
      };

      nextButton.addEventListener("click", (event) => {
        if (applicationIsMoving) return;

        if (reducedMotion || event.detail === 0) {
          jumpToApplicationSlide((applicationIndex + 1) % slides.length);
          return;
        }

        applicationIsMoving = true;
        applicationIndex += 1;
        track.classList.add("is-moving");
        setApplicationPosition(applicationIndex);
        updateApplicationA11y();
      });

      track.addEventListener("transitionend", (event) => {
        if (event.target !== track || event.propertyName !== "transform") return;

        if (applicationIndex === slides.length) {
          track.style.transition = "none";
          applicationIndex = 0;
          setApplicationPosition(applicationIndex);
          track.getBoundingClientRect();
          window.requestAnimationFrame(() => {
            track.style.transition = "";
            track.classList.remove("is-moving");
            applicationIsMoving = false;
          });
          return;
        }

        track.classList.remove("is-moving");
        applicationIsMoving = false;
      });
    }
  }

  const ingredientsCarousel = document.querySelector("[data-ingredients-carousel]");

  if (ingredientsCarousel) {
    const slides = [...ingredientsCarousel.querySelectorAll("[data-ingredients-slide]")];
    const nextButton = ingredientsCarousel.querySelector("[data-ingredients-next]");
    const ctaText = ingredientsCarousel.querySelector("[data-ingredients-cta]");
    let isFullIngredientsOpen = false;

    const updateIngredientsCarousel = () => {
      ingredientsCarousel.classList.toggle("is-full-list", isFullIngredientsOpen);
      nextButton?.classList.toggle("is-back", isFullIngredientsOpen);
      slides.forEach((slide, index) => {
        slide.setAttribute("aria-hidden", String(index !== (isFullIngredientsOpen ? 1 : 0)));
      });
      if (ctaText) {
        ctaText.textContent = isFullIngredientsOpen
          ? ingredientsCarousel.dataset.ingredientsBackLabel || "Key Ingredients"
          : ingredientsCarousel.dataset.ingredientsLabel || "View Full Ingredient List";
      }
      nextButton?.setAttribute(
        "aria-label",
        isFullIngredientsOpen ? "Back to key ingredients" : "View full ingredient list"
      );
    };

    nextButton?.addEventListener("click", () => {
      isFullIngredientsOpen = !isFullIngredientsOpen;
      updateIngredientsCarousel();
    });

    updateIngredientsCarousel();
  }

  const productAccordion = document.querySelector(".product-info-accordions");
  const productAccordionItems = productAccordion
    ? Array.from(productAccordion.querySelectorAll(":scope > details"))
    : [];
  const productAccordionStates = new WeakMap();

  const setProductAccordionInstantly = (item, open) => {
    item.classList.add("no-motion");
    item.open = open;
    item.classList.toggle("is-open", open);
    productAccordionStates.set(item, open ? "open" : "closed");
    window.requestAnimationFrame(() => item.classList.remove("no-motion"));
  };

  const closeProductAccordion = (item, animate) => {
    const state = productAccordionStates.get(item);
    if (state === "closed" || !item.open) return;
    if (!animate) {
      setProductAccordionInstantly(item, false);
      return;
    }
    productAccordionStates.set(item, "closing");
    item.classList.remove("is-open");
  };

  const openProductAccordion = (item, animate) => {
    if (!animate) {
      setProductAccordionInstantly(item, true);
      return;
    }
    item.open = true;
    productAccordionStates.set(item, "opening");
    window.requestAnimationFrame(() => {
      if (productAccordionStates.get(item) !== "opening") return;
      item.classList.add("is-open");
    });
  };

  productAccordionItems.forEach((item) => {
    productAccordionStates.set(item, item.open ? "open" : "closed");
    item.classList.toggle("is-open", item.open);

    const content = item.querySelector(":scope > .accordion-content");
    content?.addEventListener("transitionend", (event) => {
      if (event.target !== content || event.propertyName !== "grid-template-rows") return;
      const state = productAccordionStates.get(item);
      if (state === "closing") {
        item.open = false;
        productAccordionStates.set(item, "closed");
      } else if (state === "opening") {
        productAccordionStates.set(item, "open");
      }
    });

    item.querySelector("summary")?.addEventListener("click", (event) => {
      event.preventDefault();
      const state = productAccordionStates.get(item);
      const isExpanded = state === "open" || state === "opening";
      const animate = !reducedMotion && event.detail !== 0;

      if (isExpanded) {
        closeProductAccordion(item, animate);
        return;
      }

      productAccordionItems.forEach((other) => {
        if (other !== item) closeProductAccordion(other, animate);
      });
      openProductAccordion(item, animate);
    });
  });

  const accordionLists = document.querySelectorAll(".accordion-list:not(.product-info-accordions)");
  const accordionStates = new WeakMap();

  const getAccordionContent = (item) => item.querySelector(":scope > .accordion-content");

  const setAccordionInstantly = (item, open) => {
    const content = getAccordionContent(item);
    item.classList.add("no-motion");
    item.open = open;
    item.classList.toggle("is-open", open);
    if (content) content.style.height = "";
    accordionStates.set(item, open ? "open" : "closed");
    window.requestAnimationFrame(() => item.classList.remove("no-motion"));
  };

  const closeAccordion = (item, animate) => {
    const state = accordionStates.get(item);
    if (state === "closed" || !item.open) return;
    const content = getAccordionContent(item);
    if (!animate) {
      setAccordionInstantly(item, false);
      return;
    }
    if (!content) {
      item.open = false;
      accordionStates.set(item, "closed");
      return;
    }

    content.style.height = `${content.getBoundingClientRect().height}px`;
    content.getBoundingClientRect();
    accordionStates.set(item, "closing");
    window.requestAnimationFrame(() => {
      if (accordionStates.get(item) !== "closing") return;
      item.classList.remove("is-open");
      content.style.height = "0px";
    });
  };

  const openAccordion = (item, animate) => {
    const state = accordionStates.get(item);
    if (state === "open" || state === "opening") return;
    const content = getAccordionContent(item);
    if (!animate) {
      setAccordionInstantly(item, true);
      return;
    }
    if (!content) {
      item.open = true;
      accordionStates.set(item, "open");
      return;
    }

    const startHeight = item.open ? content.getBoundingClientRect().height : 0;
    item.open = true;
    content.style.height = "auto";
    const targetHeight = content.getBoundingClientRect().height;
    content.style.height = `${startHeight}px`;
    content.getBoundingClientRect();
    accordionStates.set(item, "opening");
    window.requestAnimationFrame(() => {
      if (accordionStates.get(item) !== "opening") return;
      item.classList.add("is-open");
      content.style.height = `${targetHeight}px`;
    });
  };

  accordionLists.forEach((list) => {
    const accordionItems = list.querySelectorAll(":scope > details");

    accordionItems.forEach((item) => {
      const content = getAccordionContent(item);
      accordionStates.set(item, item.open ? "open" : "closed");
      item.classList.toggle("is-open", item.open);

      content?.addEventListener("transitionend", (event) => {
        if (event.target !== content || event.propertyName !== "height") return;
        const state = accordionStates.get(item);
        if (state === "closing") {
          item.open = false;
          content.style.height = "";
          accordionStates.set(item, "closed");
        } else if (state === "opening") {
          content.style.height = "";
          accordionStates.set(item, "open");
        }
      });

      const summary = item.querySelector("summary");
      summary?.addEventListener("click", (event) => {
        event.preventDefault();
        const state = accordionStates.get(item);
        const isExpanded = state === "open" || state === "opening";
        const animate = !reducedMotion && event.detail !== 0;

        if (isExpanded) {
          closeAccordion(item, animate);
          return;
        }

        accordionItems.forEach((other) => {
          if (other !== item) closeAccordion(other, animate);
        });
        openAccordion(item, animate);
      });
    });
  });

  const journalFilter = document.querySelector("[data-journal-filter]");
  const journalGrid = document.querySelector("[data-journal-grid]");

  if (journalFilter && journalGrid) {
    const filterLinks = Array.from(journalFilter.querySelectorAll("[data-category]"));
    const journalCards = Array.from(journalGrid.querySelectorAll(".journal-card[data-category]"));
    const journalEmpty = journalGrid.querySelector("[data-journal-empty]");
    const journalIndex = journalGrid.closest(".journal-index");

    const setJournalCategory = (category, options = {}) => {
      const { updateHash = false, scroll = false } = options;
      const activeCategory = filterLinks.some((link) => link.dataset.category === category) ? category : "";
      let visibleCount = 0;

      journalCards.forEach((card) => {
        const isVisible = !activeCategory || card.dataset.category === activeCategory;
        card.hidden = !isVisible;
        if (isVisible) visibleCount += 1;
      });

      filterLinks.forEach((link) => {
        const isActive = activeCategory && link.dataset.category === activeCategory;
        link.classList.toggle("is-active", Boolean(isActive));
        if (isActive) {
          link.setAttribute("aria-current", "true");
        } else {
          link.removeAttribute("aria-current");
        }
      });

      if (journalEmpty) {
        journalEmpty.hidden = visibleCount !== 0;
      }

      if (updateHash) {
        const nextUrl = activeCategory ? `#${activeCategory}` : `${window.location.pathname}${window.location.search}`;
        window.history.replaceState(null, "", nextUrl);
      }

      if (scroll && journalIndex) {
        journalIndex.scrollIntoView({
          behavior: reducedMotion ? "auto" : "smooth",
          block: "start"
        });
      }
    };

    journalFilter.addEventListener("click", (event) => {
      const target = event.target instanceof Element ? event.target : null;
      const link = target?.closest("[data-category]");
      if (!link) return;

      event.preventDefault();
      setJournalCategory(link.dataset.category, { updateHash: true, scroll: true });
    });

    setJournalCategory(window.location.hash.replace("#", ""));
    window.addEventListener("hashchange", () => {
      setJournalCategory(window.location.hash.replace("#", ""));
    });
  }

  let accordionResizeFrame;
  window.addEventListener("resize", () => {
    window.cancelAnimationFrame(accordionResizeFrame);
    accordionResizeFrame = window.requestAnimationFrame(() => {
      document.querySelectorAll(".accordion-list details[open]").forEach((item) => {
        const content = getAccordionContent(item);
        if (!content) return;
        item.classList.add("no-motion");
        window.requestAnimationFrame(() => item.classList.remove("no-motion"));
      });
    });
  });
});
